import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FaHome, FaUserCircle, FaUsers, FaComments } from "react-icons/fa";
import axios from "axios";
import { FaSearch, FaUserGraduate } from "react-icons/fa";
import Navbar from "../components/Navbar";

const API_BASE = process.env.REACT_APP_URL;

// Adds JWT token to axios requests
const authConfig = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` }
});

export default function Admin() {
  const navigate = useNavigate();
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [userName, setUserName] = useState("");
  const [userPicture, setUserPicture] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [userCount, setUserCount] = useState(0);
  const [feedbackCount, setFeedbackCount] = useState(0);
  const [darkMode, setDarkMode] = useState(false);

  useEffect(() => {
    const syncTheme = () => {
      const savedMode = localStorage.getItem("darkMode");
      setDarkMode(savedMode ? JSON.parse(savedMode) : false);
    };
    syncTheme();
    window.addEventListener("themeChanged", syncTheme);
    return () => window.removeEventListener("themeChanged", syncTheme);
  }, []);

  const sidebarRef = useRef(null);

  useEffect(() => {
    const storedEmail = localStorage.getItem("adminEmail");
    const storedName = localStorage.getItem("userName");
    const storedPicture = localStorage.getItem("userPicture");

    if (!storedEmail) {
      navigate("/");
      return;
    }

    if (storedEmail === "admin@navriti.com") {
      setIsAuthorized(true);
      setUserEmail(storedEmail);
      setUserName("Admin");
      setUserPicture(storedPicture || "");
      localStorage.setItem("userName", "Admin");
      // user_id should already be set from LoginModal
      return;
    }

    axios.get(`${API_BASE}/admins/check/${storedEmail}`, authConfig())
      .then((res) => {
        if (res.data.isAdmin) {
          setIsAuthorized(true);
          setUserEmail(storedEmail);
          setUserName("Admin");
          localStorage.setItem("adminEmail", storedEmail);
          // user_id should already be set from LoginModal
        } else {
          navigate("/");
        }
      })
      .catch(() => navigate("/"));
  }, [navigate]);

  useEffect(() => {
    if (isAuthorized) {
      axios.get(`${API_BASE}/count`, authConfig())
        .then((res) => {
          setUserCount(res.data.users || 0);
          setFeedbackCount(res.data.feedback || 0);
        })
        .catch((err) => console.error("Error fetching counts:", err));
    }
  }, [isAuthorized]);

  if (!isAuthorized) return null;

  return (
    <div className={darkMode ? "app dark-mode" : "app"} style={styles.page}>

      <Navbar />

      <div style={{ marginTop: "0px" }}>
        <div style={styles.mainContainer}>
          <div style={styles.card}>
            <h1 style={styles.heading}>Admin Dashboard</h1>
            <p style={styles.subText}>
              Welcome, {userName || "Admin"} 👋 — You have access to manage the AI
              Detector system.
            </p>
          </div>

          <div style={styles.statsContainer}>
            <div
              style={{ ...styles.statCard, cursor: "pointer" }}
              onClick={() => navigate("/admin/users")}
            >
              <FaUsers size={35} style={styles.statIcon} />
              <h3 style={styles.statTitle}>Total Users</h3>
              <p style={styles.statValue}>{userCount}</p>
            </div>

            <div
              style={{ ...styles.statCard, cursor: "pointer" }}
              onClick={() => navigate("/admin/feedback")}
            >
              <FaComments size={35} style={styles.statIcon} />
              <h3 style={styles.statTitle}>Feedback</h3>
              <p style={styles.statValue}>{feedbackCount}</p>
            </div>

            <div
              style={{ ...styles.statCard, cursor: "pointer" }}
              onClick={() => navigate("/admin/detector")}
            >
              <FaSearch size={35} style={styles.statIcon} />
              <h3 style={{ ...styles.statTitle, marginTop: "20px" }}>
                SourceCheck
              </h3>
              <p style={styles.statValue}>Open</p>
            </div>

            {userEmail === "admin@navriti.com" && (
              <div
                style={{ ...styles.statCard, cursor: "pointer" }}
                onClick={() => navigate("/admin/manage")}
              >
                <FaUserGraduate size={35} style={styles.statIcon} />
                <h3 style={{ ...styles.statTitle, marginTop: "20px" }}>
                  Manage Admins
                </h3>
                <p style={styles.statValue}>Open</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <footer style={{
        width: "100%",
        backgroundColor: "#636CCB",
        color: "#fff",
        height: "40px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "1em",
        fontWeight: "600",
        position: "fixed",
        bottom: 0,
        left: 0
      }}>
        Navriti Technologies © All rights reserved
      </footer>

    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    fontFamily: "Poppins, sans-serif",
    overflowX: "hidden",
    paddingBottom: "150px",
  },
  navbar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: "#636CCB",
    color: "white",
    padding: "10px 30px",
    boxShadow: "0 3px 10px rgba(0, 0, 0, 0.1)",
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    height: "60px",
    zIndex: 1001,
  },
  navLeft: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  logo: { height: "35px", width: "35px", borderRadius: "50%" },
  brand: { fontSize: "1.4em", fontWeight: "bold" },
  navRight: {
    display: "flex",
    alignItems: "center",
    gap: "25px",
  },
  icon: { fontSize: "1.5em", cursor: "pointer" },
  profileWrapper: { position: "relative" },
  profilePic: {
    height: "40px",
    width: "40px",
    borderRadius: "50%",
    cursor: "pointer",
    border: "2px solid white",
    objectFit: "cover",
  },
  defaultProfileIcon: { fontSize: "2em", cursor: "pointer" },
  dropdown: {
    position: "absolute",
    top: "50px",
    right: 0,
    backgroundColor: "white",
    color: "#333",
    borderRadius: "8px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
    width: "220px",
    padding: "15px",
    zIndex: 10,
  },
  dropdownName: {
    fontWeight: "600",
    marginBottom: "3px",
    textAlign: "center",
  },
  dropdownEmail: {
    fontSize: "0.9em",
    color: "#666",
    marginBottom: "10px",
  },
  logoutButton: {
    backgroundColor: "#636CCB",
    color: "white",
    border: "none",
    borderRadius: "6px",
    padding: "8px",
    cursor: "pointer",
    width: "100%",
    fontWeight: "500",
  },
  mainContainer: {
    textAlign: "center",
    marginTop: "150px",
    paddingBottom: "120px",
  },
  card: {
    background: "var(--card-bg)",
    margin: "0 auto",
    padding: "40px 30px",
    width: "70%",
    borderRadius: "15px",
    boxShadow: "0 5px 15px rgba(0,0,0,0.1)",
    transition: "background 0.3s, color 0.3s",
  },
  heading: {
    fontSize: "2.5em",
    fontWeight: "700",
    color: "var(--text-main)",
  },
  subText: {
    color: "var(--text-sub)",
    fontSize: "1.1em",
  },
  statsContainer: {
    display: "flex",
    justifyContent: "center",
    gap: "30px",
    marginTop: "30px",
    flexWrap: "wrap",
  },
  statCard: {
    background: "linear-gradient(135deg, #8CA9FF, #636CCB)",
    color: "white",
    borderRadius: "15px",
    padding: "25px",
    width: "230px",
    textAlign: "center",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
  },
  statIcon: { marginBottom: "10px" },
  statTitle: {
    fontSize: "1.2em",
    marginBottom: "5px",
  },
  statValue: {
    fontSize: "1.8em",
    fontWeight: "bold",
  },
};